import React, { useEffect, useState, useRef } from "react";
import { Chart } from "react-chartjs-2";
import 'chartjs-adapter-date-fns';
import {
    Chart as ChartJS,
    LinearScale,
    TimeScale,
    CategoryScale,
    Title,
    Tooltip,
    Legend
} from "chart.js";
import { CandlestickController, CandlestickElement } from "chartjs-chart-financial";
import zoomPlugin from 'chartjs-plugin-zoom';

ChartJS.register(
    CategoryScale,
    LinearScale,
    TimeScale,
    Title,
    Tooltip,
    Legend,
    CandlestickController,
    CandlestickElement,
    zoomPlugin
);

// Error Boundary
class ChartErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true };
    }
    render() {
        if (this.state.hasError) {
            return <div>차트 렌더링 중 오류가 발생했습니다.</div>;
        }
        return this.props.children;
    }
}

function StockChart({ companyName }) {
    const [chartData, setChartData] = useState([]);
    const chartRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        fetch(`${process.env.REACT_APP_SPRING_API_URL}/api/stock-data?company=${encodeURIComponent(companyName)}`)
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) setChartData(data);
                else if (data.data && Array.isArray(data.data)) setChartData(data.data);
                else setChartData([]);
            })
            .catch(err => {
                console.error("데이터 로딩 실패:", err);
                setError(err);
            })
            .finally(() => setLoading(false));
    }, [companyName]);

    const candlestickData = chartData.map(d => {
        const o = parseFloat(d.Open);
        const h = parseFloat(d.High);
        const l = parseFloat(d.Low);
        const c = parseFloat(d.Close);
        if ([o, h, l, c].some(v => isNaN(v))) return null;
        return { x: new Date(d.Date), o: o.toFixed(2), h: h.toFixed(2), l: l.toFixed(2), c: c.toFixed(2) };
    }).filter(Boolean);

    if (loading) return <div>📊 데이터 로딩 중...</div>;
    if (error) return <div>❌ 데이터를 불러오는 중 오류가 발생했습니다.</div>;
    if (candlestickData.length === 0) return <div>⚠️ 표시할 데이터가 없습니다.</div>;

    const data = {
        datasets: [
            {
                label: `${companyName} 주가`,
                data: candlestickData,
                borderColor: candlestickData.map(d => d.c <= d.o ? "#FF0000" : "#0000FF"),
                backgroundColor: candlestickData.map(d => d.c <= d.o ? "#FFAAAA" : "#AAAAFF"),
                barPercentage: 0.005,
                categoryPercentage: 0.2
            }
        ]
    };

    const firstDate = candlestickData[0].x.getTime();
    const lastDate = candlestickData[candlestickData.length - 1].x.getTime();

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true },
            title: { display: true, text: `${companyName} 캔들스틱 차트` },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        const d = context.raw;
                        const color = d.c >= d.o ? '상승(빨강)' : '하락(파랑)'; // 표시용
                        return [
                            `시가: ${d.o}`,
                            `종가: ${d.c}`,
                            `고가: ${d.h}`,
                            `저가: ${d.l}`
                        ];
                    }
                }
            },
            zoom: {
                pan: {
                    enabled: true,
                    mode: 'x',
                    onPan: ({ chart }) => {
                        const xScale = chart.scales.x;
                        const range = xScale.max - xScale.min;
                        const firstDate = chart.data.datasets[0].data[0].x.getTime();
                        const lastDate = chart.data.datasets[0].data[chart.data.datasets[0].data.length - 1].x.getTime();

                        if (xScale.min < firstDate) {
                            xScale.options.min = firstDate;
                            xScale.options.max = firstDate + range;
                        }
                        if (xScale.max > lastDate) {
                            xScale.options.max = lastDate;
                            xScale.options.min = lastDate - range;
                        }

                        chart.update('none');
                    }
                }
            }
        },
        scales: {
            x: {
                type: 'time',
                time: { unit: 'day', tooltipFormat: 'yyyy-MM-dd', stepSize: 1 },
                ticks: { maxRotation: 0, autoSkip: false }
            },
            y: { beginAtZero: false }
        }
    };

    const handleZoom = (type) => {
        const chart = chartRef.current;
        if (!chart) return;

        const xScale = chart.scales.x;
        const range = xScale.max - xScale.min;
        const zoomFactor = 0.2;

        let newMin = xScale.min;
        let newMax = xScale.max;

        if (type === 'in') {
            newMin = xScale.min + range * zoomFactor / 2;
            newMax = xScale.max - range * zoomFactor / 2;
        } else {
            newMin = xScale.min - range * zoomFactor / 2;
            newMax = xScale.max + range * zoomFactor / 2;
        }

        // X축 범위 제한
        newMin = Math.max(firstDate, newMin);
        newMax = Math.min(lastDate, newMax);
        xScale.options.min = newMin;
        xScale.options.max = newMax;

        // 캔들 폭 최소값 제한
        chart.data.datasets.forEach(ds => {
            const minBar = 0.005;
            const minCategory = 0.02;
            if (type === 'in') {
                ds.barPercentage += 0.005;
                ds.categoryPercentage += 0.01;
            } else {
                ds.barPercentage = Math.max(minBar, ds.barPercentage - 0.005);
                ds.categoryPercentage = Math.max(minCategory, ds.categoryPercentage - 0.01);
            }
        });

        chart.update('none');
    };

    return (
        <div>
            <div style={{ marginBottom: '10px' }}>
                <button onClick={() => handleZoom('in')}>➕ 확대</button>
                <button onClick={() => handleZoom('out')}>➖ 축소</button>
            </div>
            <div style={{ width: "100%", overflowX: "auto" }}>
                <div style={{ width: "950px", height: "500px" }}>
                    <Chart
                        ref={chartRef}
                        key={candlestickData.length}
                        type="candlestick"
                        data={data}
                        options={options}
                    />
                </div>
            </div>
        </div>
    );
}

export default function StockChartWithBoundary(props) {
    return (
        <ChartErrorBoundary>
            <StockChart {...props} />
        </ChartErrorBoundary>
    );
}
